import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaSearch,
  FaPlus,
  FaChevronLeft,
  FaChevronRight,
  FaUser,
} from "react-icons/fa";
import useAxios from "../api/useAxios";
import Swal from "sweetalert2";

const Customers = () => {
  const axiosInstance = useAxios();
  const navigate = useNavigate();

  /* ================= STATE ================= */
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editCustomerId, setEditCustomerId] = useState(null);
  const [form, setForm] = useState({
    fullName: "",
    mobileNumber: "",
    email: "",
    password: "",
    active: true, // For posting to API
  });
  const [errors, setErrors] = useState({});

  /* ================= FETCH CUSTOMERS ================= */
  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/users");
      
      // Check if API response is successful
      if (res.data?.success) {
        // Filter only CUSTOMER role from the backend response
        if (res.data.data && Array.isArray(res.data.data)) {
          const customerUsers = res.data.data.filter(
            (user) => user.role === "CUSTOMER" || user.role === undefined
          );
          setCustomers(customerUsers);
        } else {
          setCustomers([]);
        }
      } else {
        // API returned failure
        setCustomers([]);
        Swal.fire({
          icon: 'warning',
          title: 'Warning',
          text: res.data?.message || 'Failed to load customers data',
          confirmButtonColor: '#f59e0b',
        });
      }
    } catch (err) {
      console.error("Failed to load customers", err);
      setCustomers([]);
      
      if (err.response?.data?.message) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response.data.message,
          confirmButtonColor: '#0d59f2',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load customers. Please try again.',
          confirmButtonColor: '#0d59f2',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  /* ================= VALIDATION ================= */
  const validateForm = () => {
    const newErrors = {};

    if (!form.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email))
      newErrors.email = "Email is invalid";

    if (!form.mobileNumber.trim())
      newErrors.mobileNumber = "Mobile number is required";
    else if (!/^[0-9]{10}$/.test(form.mobileNumber.replace(/\D/g, "")))
      newErrors.mobileNumber = "Mobile number must be 10 digits";

    if (!editCustomerId && !form.password)
      newErrors.password = "Password is required";
    else if (!editCustomerId && form.password.length < 6)
      newErrors.password = "Password must be at least 6 characters";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ================= FORMAT PHONE NUMBER ================= */
  const formatPhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    }
    return phone;
  };

  /* ================= FILTER + PAGINATION ================= */
  const filtered = customers.filter(
    (c) =>
      (c.fullName || c.name || "")
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      (c.email || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.mobileNumber || c.mobile || "")
        .toLowerCase()
        .includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentCustomers = filtered.slice(startIndex, startIndex + rowsPerPage);

  /* ================= OPEN ADD ================= */
  const openAddModal = () => {
    setEditCustomerId(null);
    setForm({
      fullName: "",
      mobileNumber: "",
      email: "",
      password: "",
      active: true, // For posting
    });
    setErrors({});
    setShowModal(true);
  };

  /* ================= OPEN EDIT ================= */
  const openEditModal = (customer) => {
    setEditCustomerId(customer.id);
    // Use isActive from response, fallback to active, then default to true
    const customerIsActive = customer.isActive !== undefined ? 
                            customer.isActive : 
                            (customer.active !== false);
    
    setForm({
      fullName: customer.fullName || customer.name || "",
      mobileNumber: customer.mobileNumber || customer.mobile || "",
      email: customer.email || "",
      password: "",
      active: customerIsActive, // Map isActive from response to active for posting
    });
    setErrors({});
    setShowModal(true);
  };

  /* ================= SAVE (ADD / UPDATE) ================= */
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      let response;
      if (editCustomerId) {
        // UPDATE - Use 'active' for posting
        response = await axiosInstance.put(`/users/${editCustomerId}`, {
          fullName: form.fullName,
          mobileNumber: form.mobileNumber,
          email: form.email,
          active: form.active, // Post as 'active'
          role: "CUSTOMER",
        });
      } else {
        // CREATE - Always CUSTOMER role, use 'active' for posting
        response = await axiosInstance.post("/users/register", {
          fullName: form.fullName,
          mobileNumber: form.mobileNumber,
          email: form.email,
          password: form.password,
          role: "CUSTOMER",
          active: form.active, // Post as 'active'
        });
      }

      // Check if the API response indicates success
      if (response.data?.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: response.data.message || (editCustomerId ? 'Customer updated successfully' : 'Customer created successfully'),
          confirmButtonColor: '#0d59f2',
          timer: 2000,
          timerProgressBar: true,
        });
        
        setShowModal(false);
        fetchCustomers();
      } else {
        // API returned failure (success: false)
        Swal.fire({
          icon: 'warning',
          title: 'Warning',
          text: response.data?.message || 'Operation completed with warnings',
          confirmButtonColor: '#f59e0b',
        });
      }
    } catch (err) {
      console.error("Save failed", err);
      
      // Check if it's a validation error from backend
      if (err.response?.data?.message) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response.data.message,
          confirmButtonColor: '#0d59f2',
        });
      } else if (err.response?.data?.errors) {
        // Handle validation errors array
        const errorMessages = Object.values(err.response.data.errors)
          .flat()
          .map((error, index) => `${index + 1}. ${error}`)
          .join('\n');
        
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          html: `<div class="text-left"><p class="mb-2">Please fix the following issues:</p><pre class="text-sm bg-gray-100 p-3 rounded whitespace-pre-wrap">${errorMessages}</pre></div>`,
          confirmButtonColor: '#0d59f2',
        });
      } else if (err.message === "Network Error") {
        Swal.fire({
          icon: 'error',
          title: 'Network Error',
          text: 'Unable to connect to server. Please check your internet connection.',
          confirmButtonColor: '#0d59f2',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to save customer. Please try again.',
          confirmButtonColor: '#0d59f2',
        });
      }
    }
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id, customerName) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete Customer',
      html: `
        <div class="text-left">
          <p class="mb-3">Are you sure you want to delete <strong>${customerName}</strong>?</p>
          <div class="bg-red-50 border-l-4 border-red-500 p-3 mb-3">
            <div class="flex items-start">
              <div class="text-red-500 mr-2 mt-0.5">⚠️</div>
              <div>
                <span class="text-red-700 font-medium">Warning: This action cannot be undone!</span>
                <p class="text-sm text-red-600 mt-1">All data associated with this customer will be permanently deleted, including:</p>
                <ul class="text-sm text-red-600 mt-1 ml-4 list-disc">
                  <li>Customer profile information</li>
                  <li>Order history</li>
                  <li>Purchase records</li>
                  <li>Login credentials</li>
                </ul>
              </div>
            </div>
          </div>
          <p class="text-sm text-gray-600">Type <strong>DELETE</strong> below to confirm:</p>
        </div>
      `,
      input: 'text',
      inputPlaceholder: 'Type DELETE to confirm',
      showCancelButton: true,
      confirmButtonText: 'Delete Customer',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      showLoaderOnConfirm: true,
      preConfirm: (input) => {
        if (input !== 'DELETE') {
          Swal.showValidationMessage('Please type DELETE to confirm deletion');
          return false;
        }
        return true;
      },
    });

    if (result.isConfirmed) {
      try {
        const response = await axiosInstance.delete(`/users/${id}`);
        
        if (response.data?.success) {
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: response.data.message || 'Customer has been deleted successfully',
            confirmButtonColor: '#0d59f2',
            timer: 2000,
            timerProgressBar: true,
          });
          
          fetchCustomers();
        } else {
          Swal.fire({
            icon: 'warning',
            title: 'Warning',
            text: response.data?.message || 'Delete operation completed with warnings',
            confirmButtonColor: '#f59e0b',
          });
        }
      } catch (err) {
        console.error("Delete failed", err);
        
        if (err.response?.data?.message) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: err.response.data.message,
            confirmButtonColor: '#0d59f2',
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to delete customer',
            confirmButtonColor: '#0d59f2',
          });
        }
      }
    }
  };

  /* ================= HANDLE INPUT CHANGE ================= */
  const handleInputChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  /* ================= PAGINATION CONTROLS ================= */
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      const half = Math.floor(maxVisiblePages / 2);
      let start = currentPage - half;
      let end = currentPage + half;

      if (start < 1) {
        start = 1;
        end = maxVisiblePages;
      }
      if (end > totalPages) {
        end = totalPages;
        start = Math.max(1, totalPages - maxVisiblePages + 1);
      }

      for (let i = start; i <= end; i++) pageNumbers.push(i);
    }

    return pageNumbers;
  };

  /* ================= RESET PAGINATION ON SEARCH ================= */
  useEffect(() => {
    setCurrentPage(1);
  }, [search, rowsPerPage]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 md:p-6 lg:p-8">
      {/* HEADER */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Customers</h1>
            <p className="text-gray-600 mt-2">
              Manage your customer accounts and information
            </p>
          </div>

          <button
            onClick={openAddModal}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-3 shadow-lg hover:shadow-xl"
          >
            <FaPlus className="text-lg" />
            <span>Add New Customer</span>
          </button>
        </div>

        {/* SEARCH AND CONTROLS */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="relative flex-1">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                placeholder="Search customers by name, email, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">Show:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  className="border-2 border-gray-200 rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                >
                  <option value="5">5 rows</option>
                  <option value="10">10 rows</option>
                  <option value="25">25 rows</option>
                  <option value="50">50 rows</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TABLE CONTAINER */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-blue-50">
              <tr>
                <th className="px-8 py-5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <span>#</span>
                    <span>ID</span>
                  </div>
                </th>
                <th className="px-8 py-5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Customer Details
                </th>
                <th className="px-8 py-5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Contact Info
                </th>
                <th className="px-8 py-5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-8 py-5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-8 py-6">
                      <div className="h-4 bg-gray-200 rounded w-8"></div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                        <div className="h-3 bg-gray-100 rounded w-24"></div>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-40"></div>
                        <div className="h-3 bg-gray-100 rounded w-28"></div>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="h-8 bg-gray-200 rounded-full w-20"></div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex gap-3">
                        <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                        <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                        <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : currentCustomers.length > 0 ? (
                currentCustomers.map((c, i) => {
                  // Check both isActive and active fields from response
                  const isActive = c.isActive !== undefined ? 
                                  c.isActive : 
                                  (c.active !== false);
                  
                  return (
                    <tr
                      key={c.id}
                      className="hover:bg-gray-50/50 transition-colors group"
                    >
                      <td className="px-8 py-6">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {startIndex + i + 1}
                          </span>
                          <span className="text-xs text-gray-500">
                            ID: {c.id}
                          </span>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
                              <span className="font-semibold text-blue-600">
                                {(c.fullName || c.name || "C")[0].toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900">
                                {c.fullName || c.name || "Unknown"}
                              </h4>
                              <p className="text-sm text-gray-500 mt-1">
                                {c.role || "CUSTOMER"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">📧</span>
                            <span className="text-gray-700">
                              {c.email || "No email"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">📱</span>
                            <span className="text-gray-700">
                              {formatPhoneNumber(
                                c.mobileNumber || c.mobile || "N/A"
                              )}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex flex-col gap-2">
                          <span
                            className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                              isActive
                                ? "bg-green-50 text-green-700 border border-green-200"
                                : "bg-red-50 text-red-700 border border-red-200"
                            }`}
                          >
                            {isActive ? (
                              <>
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></span>
                                Active
                              </>
                            ) : (
                              <>
                                <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></span>
                                Inactive
                              </>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/customers-view/${c.id}`)}
                            className="p-2.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 group-hover:scale-105"
                            title="View Details"
                          >
                            <FaEye size={18} />
                          </button>

                          <button
                            onClick={() => openEditModal(c)}
                            className="p-2.5 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-xl transition-all duration-200 group-hover:scale-105"
                            title="Edit Customer"
                          >
                            <FaEdit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(c.id, c.fullName || c.name || "this customer")}
                            className="p-2.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 group-hover:scale-105"
                            title="Delete Customer"
                          >
                            <FaTrash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="px-8 py-16 text-center">
                    <div className="flex flex-col items-center justify-center max-w-md mx-auto">
                      <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                        <FaUser className="w-12 h-12 text-gray-400" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-700 mb-2">
                        No customers found
                      </h3>
                      <p className="text-gray-500 mb-6">
                        {search
                          ? "No customers match your search criteria."
                          : "Get started by adding your first customer."}
                      </p>
                      {!search && (
                        <button
                          onClick={openAddModal}
                          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
                        >
                          <FaPlus />
                          Add First Customer
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {filtered.length > 0 && !loading && (
          <div className="px-8 py-6 border-t border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="text-sm text-gray-600">
                Showing{" "}
                <span className="font-semibold text-gray-800">
                  {startIndex + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-gray-800">
                  {Math.min(startIndex + rowsPerPage, filtered.length)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-800">
                  {filtered.length}
                </span>{" "}
                customers
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className={`p-2.5 rounded-xl border transition-all ${
                    currentPage === 1
                      ? "border-gray-200 text-gray-400 cursor-not-allowed"
                      : "border-gray-300 text-gray-700 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50"
                  }`}
                  title="Previous"
                >
                  <FaChevronLeft />
                </button>

                <div className="flex items-center gap-1">
                  {getPageNumbers().map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        currentPage === pageNum
                          ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className={`p-2.5 rounded-xl border transition-all ${
                    currentPage === totalPages
                      ? "border-gray-200 text-gray-400 cursor-not-allowed"
                      : "border-gray-300 text-gray-700 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50"
                  }`}
                  title="Next"
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl w-full max-w-lg animate-slideUp">
            {/* Modal Header */}
            <div className="p-8 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">
                    {editCustomerId ? "Edit Customer" : "New Customer"}
                  </h3>
                  <p className="text-gray-500 mt-1">
                    {editCustomerId
                      ? "Update customer information"
                      : "Add a new customer to your system"}
                  </p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-8 max-h-[70vh] overflow-y-auto">
              <div className="space-y-6">
                {/* Row 1 */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Full Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      placeholder="John Doe"
                      value={form.fullName}
                      onChange={(e) =>
                        handleInputChange("fullName", e.target.value)
                      }
                      className={`w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none focus:ring-2 ${
                        errors.fullName
                          ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                          : "border-gray-200 focus:border-blue-500 focus:ring-blue-100"
                      }`}
                    />
                    {errors.fullName && (
                      <p className="mt-1 text-sm text-red-600">
                        ⚠️ {errors.fullName}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      placeholder="john@example.com"
                      value={form.email}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      className={`w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none focus:ring-2 ${
                        errors.email
                          ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                          : "border-gray-200 focus:border-blue-500 focus:ring-blue-100"
                      }`}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">
                        ⚠️ {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Mobile */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      placeholder="9876543210"
                      value={form.mobileNumber}
                      onChange={(e) =>
                        handleInputChange(
                          "mobileNumber",
                          e.target.value.replace(/\D/g, "")
                        )
                      }
                      className={`w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none focus:ring-2 ${
                        errors.mobileNumber
                          ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                          : "border-gray-200 focus:border-blue-500 focus:ring-blue-100"
                      }`}
                    />
                    {errors.mobileNumber && (
                      <p className="mt-1 text-sm text-red-600">
                        ⚠️ {errors.mobileNumber}
                      </p>
                    )}
                  </div>

                  {/* Password (only for new customer) */}
                  {!editCustomerId && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Password <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        placeholder="••••••••"
                        value={form.password}
                        onChange={(e) =>
                          handleInputChange("password", e.target.value)
                        }
                        className={`w-full px-4 py-3.5 border-2 rounded-xl focus:outline-none focus:ring-2 ${
                          errors.password
                            ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                            : "border-gray-200 focus:border-blue-500 focus:ring-blue-100"
                        }`}
                      />
                      {errors.password && (
                        <p className="mt-1 text-sm text-red-600">
                          ⚠️ {errors.password}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Status - Full Width */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Account Status
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => handleInputChange("active", true)}
                      className={`px-4 py-3.5 rounded-xl border-2 ${
                        form.active
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-200"
                      }`}
                    >
                      Active
                    </button>

                    <button
                      type="button"
                      onClick={() => handleInputChange("active", false)}
                      className={`px-4 py-3.5 rounded-xl border-2 ${
                        !form.active
                          ? "border-red-500 bg-red-50 text-red-700"
                          : "border-gray-200"
                      }`}
                    >
                      Inactive
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-8 border-t border-gray-100">
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-xl hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all"
                >
                  {editCustomerId ? "Update Customer" : "Create Customer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Customers;